/**
 * Version Extractor Utility
 *
 * Extracts version information from uploaded build files (ZIP, APK).
 * Used by the Dev Builds page to auto-detect versions on file upload.
 */

import JSZip from 'jszip';

export interface ExtractedVersion {
  version: string;
  source: 'manifest' | 'apk-metadata' | 'filename' | 'unknown';
  confidence: 'high' | 'medium' | 'low';
}

/**
 * Extract version from an uploaded file
 * Supports: .zip (browser extensions), .apk (Android)
 */
export async function extractVersionFromFile(file: File): Promise<ExtractedVersion | null> {
  const ext = file.name.split('.').pop()?.toLowerCase();

  try {
    if (ext === 'zip') {
      return await extractVersionFromZip(file);
    } else if (ext === 'apk') {
      return await extractVersionFromApk(file);
    }
  } catch (error) {
    console.warn('Version extraction failed:', error);
  }

  // Fallback: try to extract from filename
  return extractVersionFromFilename(file.name);
}

/**
 * Extract version from a ZIP file (browser extension)
 * Looks for manifest.json inside the ZIP
 */
async function extractVersionFromZip(file: File): Promise<ExtractedVersion | null> {
  const zip = new JSZip();
  const contents = await zip.loadAsync(file);

  // Look for manifest.json in common locations
  const manifestPaths = [
    'manifest.json',
    'chrome/manifest.json',
    'firefox/manifest.json',
    'safari/manifest.json',
  ];

  for (const manifestPath of manifestPaths) {
    const manifestFile = contents.file(manifestPath);
    if (manifestFile) {
      try {
        const text = await manifestFile.async('string');
        const manifest = JSON.parse(text);
        if (manifest.version) {
          return {
            version: manifest.version,
            source: 'manifest',
            confidence: 'high',
          };
        }
      } catch {
        // Continue to next path
      }
    }
  }

  // Fallback to filename
  return extractVersionFromFilename(file.name);
}

/**
 * Extract version from an APK file
 * APK files are ZIP archives - look for metadata files
 */
async function extractVersionFromApk(file: File): Promise<ExtractedVersion | null> {
  const zip = new JSZip();
  const contents = await zip.loadAsync(file);

  // Look for output-metadata.json (Gradle build output)
  const metadataFile = contents.file('output-metadata.json');
  if (metadataFile) {
    try {
      const text = await metadataFile.async('string');
      const metadata = JSON.parse(text);
      const versionName = metadata.elements?.[0]?.versionName;
      if (versionName) {
        return {
          version: versionName,
          source: 'apk-metadata',
          confidence: 'high',
        };
      }
    } catch {
      // Continue to fallback
    }
  }

  // Fallback to filename
  return extractVersionFromFilename(file.name);
}

/**
 * Extract version from filename using common patterns
 * e.g., "rumio-chrome-v1.0.0-build.42.zip" -> "1.0.0-build.42"
 */
function extractVersionFromFilename(filename: string): ExtractedVersion | null {
  // Pattern: v1.0.0 or v1.0.0-build.42 or v1.0.0-beta.1
  const versionMatch = filename.match(/-v([\d]+\.[\d]+\.[\d]+(?:-[\w.]+)?)\./);
  if (versionMatch) {
    return {
      version: versionMatch[1],
      source: 'filename',
      confidence: 'medium',
    };
  }

  // Pattern without v prefix: 1.0.0 or 1.0.0-build.42
  const altMatch = filename.match(/[_-]([\d]+\.[\d]+\.[\d]+(?:-[\w.]+)?)\./);
  if (altMatch) {
    return {
      version: altMatch[1],
      source: 'filename',
      confidence: 'low',
    };
  }

  return null;
}

/**
 * Validate a version string (basic semver check)
 */
export function isValidVersion(version: string): boolean {
  // Basic semver pattern: X.Y.Z or X.Y.Z-prerelease
  const semverPattern = /^\d+\.\d+\.\d+(-[\w.]+)?$/;
  return semverPattern.test(version);
}
